// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: decisions/0027-integration-grant-approval-policy-placement.md
// @greenlight-spec: decisions/0039-injected-grant-is-disclosure.md
import * as React from 'react';

import { ChevronDown } from 'lucide-react';

import { PEM_SECRET_PLACEHOLDER } from '@greenlight/shared';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

import type { SecretFieldInput } from './CredentialSecretInputs';
import { PrivateKeyEntry } from './PrivateKeyEntry';

export interface CredentialDraft {
  slug: string;
  scope: string;
  secret: string;
  approvalAuto: boolean;
  generated: boolean;
  /** Values for a structured secret's named fields, keyed by field. */
  secretFields: Record<string, string>;
}

export const EMPTY_CREDENTIAL: CredentialDraft = {
  slug: '',
  scope: '',
  secret: '',
  approvalAuto: true,
  generated: false,
  secretFields: {},
};

export interface CredentialFieldsetProps {
  credential: CredentialDraft;
  /** Database credentials are DB passwords; the labels/placeholders adapt. */
  isDatabase: boolean;
  /**
   * What the pasted secret is called in the label/placeholder ("password",
   * "client secret", "secret"); defaults by isDatabase.
   */
  secretNoun?: string;
  /** Private-key PEM (and similar) needs a multiline paste; the default is a password input. */
  secretMultiline?: boolean;
  /** Offer in-browser certificate generation (`private_key_jwt` only). */
  generateCertificate?: boolean;
  /**
   * Workload-identity connected DB: there is no secret to paste (the proxy
   * authenticates as the platform data identity — ADR-0030), so the secret input
   * is replaced with a note. The slug still names the credential identifier.
   */
  secretless?: boolean;
  secretFormatHint?: string;
  /** Catalog registration uses provider-neutral field labels and a generated editable slug. */
  catalogMode?: boolean;
  /** Catalog secret-input example (vendor-specific). Falls back in catalogMode. */
  secretPlaceholder?: string;
  /** Catalog description-input example. Falls back in catalogMode. */
  descriptionPlaceholder?: string;
  /** Injected delivery: the credential draft carries the disclosure warning (ADR-0039). */
  showEnvVar: boolean;
  /** The secret field's label; defaults from catalogMode and secretNoun. */
  secretLabel?: string;
  /** The credential identifier used when the slug is left blank. */
  defaultSlug?: string;
  /** The description used when it is left blank. */
  defaultScope?: string;
  /** Fields that identify the credential upstream (client id, username), shown above the secret. */
  identityFields?: React.ReactNode;
  error?: string;
  /** A structured secret: one write-only input per named field instead of one secret. */
  secretFields?: readonly SecretFieldInput[];
  onSecretFieldChange?: (key: string, value: string) => void;
  onChange: (key: 'slug' | 'scope' | 'secret' | 'generated', value: string | boolean) => void;
  onApproval: (value: boolean) => void;
}

/**
 * The single credential collected at registration: the write-only secret and the
 * grant-approval toggle up front, with the slug and free-text scope defaulted
 * under Advanced (open by default). Additional credentials are added after registration from the
 * integration row.
 */
export function CredentialFieldset({
  credential,
  isDatabase,
  secretNoun = isDatabase ? 'password' : 'secret',
  secretMultiline = false,
  generateCertificate = false,
  secretless = false,
  secretFormatHint,
  catalogMode = false,
  secretPlaceholder: secretPlaceholderProp,
  descriptionPlaceholder,
  showEnvVar,
  secretLabel: secretLabelProp,
  defaultSlug = 'credential',
  defaultScope,
  identityFields,
  error,
  secretFields = [],
  onSecretFieldChange,
  onChange,
  onApproval,
}: CredentialFieldsetProps): React.JSX.Element {
  const secretPlaceholder = catalogMode
    ? (secretPlaceholderProp ?? (secretMultiline ? PEM_SECRET_PLACEHOLDER : 'API key'))
    : `${secretNoun.charAt(0).toUpperCase()}${secretNoun.slice(1)} — stored in Key Vault, never read back`;
  const secretLabel =
    secretLabelProp ??
    (catalogMode ? 'Credential' : `${secretNoun.charAt(0).toUpperCase()}${secretNoun.slice(1)}`);
  const scopeLabel = catalogMode ? 'Description' : 'Scope';
  const scopeFallback =
    defaultScope ??
    (catalogMode
      ? (descriptionPlaceholder ?? 'e.g. production access')
      : isDatabase
        ? 'What this connection grants (e.g. SELECT on sales schema)'
        : 'What this key grants (free text)');
  const [showAdvanced, setShowAdvanced] = React.useState(true);
  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium text-foreground">Credential</legend>
      <p className="text-[11.5px] leading-snug text-muted-foreground">
        Registration collects one credential. Add more later from this integration’s card.
      </p>
      <div className="space-y-3" data-testid="credential-draft-0">
        {identityFields}
        {secretless ? (
          <p className="rounded-gl border border-dashed border-border px-3 py-2 text-[11.5px] leading-snug text-muted-foreground">
            {isDatabase
              ? 'No secret to paste — Greenlight connects as its platform data identity. Grant that identity access inside the database (see the Set up step).'
              : 'No secret to paste — Greenlight serves this data itself (see the Set up step).'}
          </p>
        ) : secretFields.length > 0 ? (
          secretFields.map((field) => (
            <div key={field.key} className="space-y-1.5">
              <Label htmlFor={`cred-secret-0-${field.key}`}>{field.label}</Label>
              <Input
                id={`cred-secret-0-${field.key}`}
                type="password"
                value={credential.secretFields[field.key] ?? ''}
                onChange={(e) => onSecretFieldChange?.(field.key, e.target.value)}
                placeholder={field.placeholder ?? field.label}
                autoComplete="off"
              />
            </div>
          ))
        ) : (
          <div className="space-y-1.5">
            {secretFormatHint && (
              <p
                className="rounded-gl border border-dashed border-border bg-background px-3 py-2 font-mono text-[11px] leading-snug text-muted-foreground"
                data-testid="credential-secret-format-hint"
              >
                {secretFormatHint}
              </p>
            )}
            {secretMultiline ? (
              <PrivateKeyEntry
                id="cred-secret-0"
                label={secretLabel}
                value={credential.secret}
                onChange={(privateKeyPem, generated) => {
                  onChange('secret', privateKeyPem);
                  onChange('generated', generated);
                }}
                generated={credential.generated}
                allowGenerate={generateCertificate}
                placeholder={secretPlaceholder}
              />
            ) : (
              <>
                <Label htmlFor="cred-secret-0">{secretLabel}</Label>
                <Input
                  id="cred-secret-0"
                  type="password"
                  value={credential.secret}
                  onChange={(e) => onChange('secret', e.target.value)}
                  placeholder={secretPlaceholder}
                  autoComplete="off"
                />
              </>
            )}
          </div>
        )}
        {showEnvVar && (
          <p
            className="text-[11px] leading-snug text-foreground"
            data-testid="cred-injected-disclosure-0"
          >
            Injected: granted agents and developers can obtain this value, including locally. Prefer
            a dedicated least-privilege upstream account — rotation is the only revocation.
          </p>
        )}
        <div className="flex items-center justify-between gap-3 rounded-gl border border-border px-3 py-2.5">
          <div className="space-y-0.5">
            <Label htmlFor="cred-approval-0" className="cursor-pointer text-[13px]">
              Auto-approve grants
            </Label>
            <p className="text-[11.5px] leading-snug text-muted-foreground">
              Off means IT approves each app’s request for this credential.
            </p>
          </div>
          <Switch
            id="cred-approval-0"
            checked={credential.approvalAuto}
            onCheckedChange={onApproval}
            aria-label="Auto-approve grants for credential 1"
          />
        </div>
        <div className="overflow-hidden rounded-gl border border-border">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            aria-expanded={showAdvanced}
            aria-controls="cred-advanced-0"
            onClick={() => setShowAdvanced((shown) => !shown)}
          >
            <span className="min-w-0">
              <span className="block text-[13px] font-medium text-foreground">Advanced</span>
              <span className="block truncate text-[11.5px] text-muted-foreground">
                Credential identifier{' '}
                <span className="font-mono">{credential.slug.trim() || defaultSlug}</span>
                {' · '}
                {credential.scope.trim() || defaultScope || 'no description yet'}
              </span>
            </span>
            <ChevronDown
              className={cn(
                'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                showAdvanced && 'rotate-180',
              )}
              aria-hidden="true"
            />
          </button>
          {showAdvanced && (
            <div id="cred-advanced-0" className="space-y-3 border-t border-border p-3">
              <div className="space-y-1.5">
                <Label htmlFor="cred-slug-0" className="text-xs">
                  Credential identifier
                </Label>
                <Input
                  id="cred-slug-0"
                  value={credential.slug}
                  onChange={(e) => onChange('slug', e.target.value)}
                  placeholder={defaultSlug}
                  className="font-mono"
                />
                <p className="text-[11px] text-muted-foreground">
                  What builder agents name when they request this credential. Leave blank for{' '}
                  <span className="font-mono">{defaultSlug}</span>.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cred-notes-0" className="text-xs">
                  {scopeLabel}
                </Label>
                <Input
                  id="cred-notes-0"
                  value={credential.scope}
                  onChange={(e) => onChange('scope', e.target.value)}
                  placeholder={scopeFallback}
                />
                {defaultScope && (
                  <p className="text-[11px] text-muted-foreground">
                    Builder agents read this to choose a credential: say what it reaches, such as
                    the Sales workspace and the Revenue semantic model. Leave blank for “
                    {defaultScope}”.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {error && <p className="text-[11.5px] text-destructive">{error}</p>}
    </fieldset>
  );
}
